function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import usePortal from '../lib/usePortal';
import { padding, themify } from '../styles/mixins';
const StyledTipArrow = styled.div.withConfig({
  displayName: "Tooltip__StyledTipArrow",
  componentId: "divo9c-0"
})(["position:absolute;top:", ";right:", ";bottom:", ";left:", ";width:0;height:0;border-style:solid;border-top-width:", ";border-right-width:", ";border-bottom-width:", ";border-left-width:", ";border-top-color:", ";border-right-color:", ";border-bottom-color:", ";border-left-color:", ";margin-top:", ";margin-left:", ";"], x => x.placement === 'bottom' ? '0' : x.placement === 'right' || x.placement === 'left' ? '50%' : 'auto', x => x.placement === 'left' ? '0' : 'auto', x => x.placement === 'top' || x.placement === 'left' ? '0' : 'auto', x => x.placement === 'right' ? '0' : x.placement === 'top' || x.placement === 'bottom' ? '50%' : 'auto', x => x.placement !== 'bottom' ? x.arrowSize : '0', x => x.placement !== 'left' ? x.arrowSize : '0', x => x.placement !== 'top' ? x.arrowSize : '0', x => x.placement !== 'right' ? x.arrowSize : '0', x => x.placement === 'top' ? x.bgColor : 'transparent', x => x.placement === 'right' ? x.bgColor : 'transparent', x => x.placement === 'bottom' ? x.bgColor : 'transparent', x => x.placement === 'left' ? x.bgColor : 'transparent', x => x.placement === 'left' || x.placement === 'right' ? `-${x.arrowSize}` : '0', x => x.placement === 'top' || x.placement === 'bottom' ? `-${x.arrowSize}` : '0');
const StyledTooltip = styled.div.withConfig({
  displayName: "Tooltip__StyledTooltip",
  componentId: "divo9c-1"
})(["position:absolute;top:", "px;left:", "px;color:white;border-radius:3px;z-index:", ";padding:", ";visibility:", ";"], x => x.top, x => x.left, x => x.theme.z.tooltip, x => `${x.placement === 'top' || x.placement === 'bottom' ? x.arrowSize : '0'} ${x.placement === 'left' || x.placement === 'right' ? x.arrowSize : '0'}`, x => x.top === -1 || x.left === -1 ? 'hidden' : 'visible');
const StyledTipContent = styled.div.withConfig({
  displayName: "Tooltip__StyledTipContent",
  componentId: "divo9c-2"
})(["", ";background:", ";border-radius:3px;color:", ";font-size:", ";font-weight:", ";letter-spacing:", ";max-width:", ";text-align:center;line-height:", ";box-shadow:", ";"], x => padding(x.padding), x => x.bgColor, x => x.color, x => x.theme.type.scale[x.fontSize], x => x.fontWeight, x => x.letterSpacing, x => x.maxWidth, x => x.lineHeight, x => x.withShadow ? '0 6px 6px -6px rgba(0, 0, 0, 0.5)' : 'none');

function getWindowOffsetTopLeft(el, clientWindow) {
  const rect = el.getBoundingClientRect();
  const scrollTop = clientWindow ? clientWindow.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop : 0;
  const scrollLeft = clientWindow ? clientWindow.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft : 0;
  return {
    top: rect.top + scrollTop,
    left: rect.left + scrollLeft
  };
}

export const Tooltip = (_ref) => {
  let {
    arrowSize = '6px',
    bgColor = 'grayBlack',
    block = false,
    children,
    color = 'white',
    content = '',
    delay = 0,
    disabled = false,
    fontSize = 6,
    fontWeight = 500,
    letterSpacing = '0.25px',
    lineHeight = 1.2,
    maxWidth = '288px',
    padding = 1,
    placement = 'top',
    trigger = 'hover focus touch',
    withShadow = false
  } = _ref,
      rest = _objectWithoutProperties(_ref, ["arrowSize", "bgColor", "block", "children", "color", "content", "delay", "disabled", "fontSize", "fontWeight", "letterSpacing", "lineHeight", "maxWidth", "padding", "placement", "trigger", "withShadow"]);

  const [clientWindow, setClientWindow] = useState(typeof window !== 'undefined' ? window : undefined);
  const [isVisible, setIsVisible] = useState(false);
  const [timer, setTimer] = useState(null);
  const [top, setTop] = useState(-1);
  const [left, setLeft] = useState(-1);
  const tooltipPortal = usePortal(rest.id || 'tooltip');
  const tooltipToggler = useRef(null);
  const tooltipElement = useRef(null);
  const tooltipEl = isVisible ? /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement(StyledTooltip, _extends({
    ref: tooltipElement,
    arrowSize: arrowSize,
    placement: placement,
    top: top,
    left: left,
    role: "tooltip"
  }, rest), /*#__PURE__*/React.createElement(StyledTipArrow, {
    arrowSize: arrowSize,
    bgColor: themify(bgColor),
    placement: placement
  }), /*#__PURE__*/React.createElement(StyledTipContent, {
    bgColor: themify(bgColor),
    color: themify(color),
    fontSize: fontSize,
    fontWeight: fontWeight,
    lineHeight: lineHeight // eslint-disable-next-line @typescript-eslint/no-use-before-define
    ,
    onMouseDown: trigger.indexOf('click') > -1 ? () => hideTooltip : undefined,
    padding: padding,
    maxWidth: maxWidth,
    withShadow: withShadow,
    letterSpacing: letterSpacing
  }, content || '')), tooltipPortal) : /*#__PURE__*/React.createElement(React.Fragment, null);
  useEffect(() => {
    if (!clientWindow && window) {
      setClientWindow(window);
    }
  }, [clientWindow, setClientWindow]);
  const updatePosition = useCallback(() => {
    if (!tooltipToggler.current || !tooltipElement.current) return;
    const offset = getWindowOffsetTopLeft(tooltipToggler.current, clientWindow);

    if (placement === 'top') {
      setTop(offset.top - tooltipElement.current.offsetHeight);
      setLeft(offset.left + (tooltipToggler.current.offsetWidth / 2 - tooltipElement.current.offsetWidth / 2));
    } else if (placement === 'bottom') {
      setTop(offset.top + tooltipToggler.current.offsetHeight);
      setLeft(offset.left + (tooltipToggler.current.offsetWidth / 2 - tooltipElement.current.offsetWidth / 2));
    } else if (placement === 'right') {
      setTop(offset.top + (tooltipToggler.current.offsetHeight / 2 - tooltipElement.current.offsetHeight / 2));
      setLeft(offset.left + tooltipToggler.current.offsetWidth);
    } else {
      setTop(offset.top + (tooltipToggler.current.offsetHeight / 2 - tooltipElement.current.offsetHeight / 2));
      setLeft(offset.left - tooltipElement.current.offsetWidth);
    }
  }, [clientWindow, placement]);
  const showTooltip = useCallback(forceUpdate => {
    if (isVisible && !forceUpdate) return;
    if (clientWindow && timer && typeof timer === 'number') clientWindow.clearTimeout(timer);else if (timer && typeof timer !== 'number') clearTimeout(timer);
    const timeout = clientWindow ? clientWindow.setTimeout(updatePosition, delay) : setTimeout(updatePosition, delay);
    setTimer(timeout);
    setIsVisible(true);
  }, [delay, isVisible, timer, updatePosition, clientWindow]);
  const hideTooltip = useCallback(() => {
    if (!isVisible) return;
    if (clientWindow && timer && typeof timer === 'number') clientWindow.clearTimeout(timer);else if (timer && typeof timer !== 'number') clearTimeout(timer);
    setIsVisible(false);
    setTimer(null);
  }, [isVisible, timer, clientWindow]);
  const toggleTooltip = useCallback(() => {
    if (isVisible) hideTooltip();else showTooltip();
  }, [isVisible, hideTooltip, showTooltip]);

  const _hide = useCallback((callback, e = undefined) => {
    hideTooltip();
    if (callback) return callback(e);
  }, [hideTooltip]);

  const _toggle = useCallback((callback, e) => {
    toggleTooltip();
    if (callback) return callback(e);
  }, [toggleTooltip]);

  const _show = useCallback((callback, e = undefined) => {
    showTooltip();
    if (callback) return callback(e);
  }, [showTooltip]);

  const child = React.Children.only(children);
  const {
    onBlur,
    onClick,
    onFocus,
    onMouseLeave,
    onMouseEnter,
    onTouchEnd,
    onTouchStart
  } = child.props;
  const triggerProps = useMemo(() => {
    const triggers = trigger.split(' ');
    const props = {};

    if (!disabled) {
      triggers.forEach(trig => {
        if (trig === 'click') {
          props.onClick = () => _toggle(onClick);
        } else if (trig === 'hover') {
          props.onMouseEnter = () => _show(onMouseEnter);

          props.onMouseLeave = () => _hide(onMouseLeave);
        } else if (trig === 'focus') {
          props.onFocus = () => _show(onFocus);

          props.onBlur = () => _hide(onBlur);
        } else if (trig === 'touch') {
          props.onTouchStart = () => _show(onTouchStart);

          props.onTouchEnd = () => _hide(onTouchEnd);
        }
      });
    }

    return props;
  }, [_hide, _show, _toggle, disabled, onBlur, onClick, onFocus, onMouseEnter, onMouseLeave, onTouchEnd, onTouchStart, trigger]);
  return /*#__PURE__*/React.createElement("span", _extends({
    ref: tooltipToggler,
    style: {
      display: block ? 'block' : 'inline-block',
      pointerEvents: 'all'
    }
  }, triggerProps), children, tooltipEl);
};